import { riverChainData, riverConfigFor } from '@1delta/data-sdk'
import { RiverStabilityPoolAbi, RiverTroveManagerAbi } from '@1delta/abis'
import { multicallRetryUniversal } from '@1delta/providers'
import type { RiverMarketRaw, RiverMarketsRaw } from './types'

// 6 reads per TroveManager, in this order (keep in sync with the slicer).
const READS_PER_TM = 6

// Merged ABI: TM reads + the diamond's SP read (function names are unique).
const RIVER_PUBLIC_READ_ABI = [
  ...RiverTroveManagerAbi,
  ...RiverStabilityPoolAbi,
] as any

/**
 * Fetch all market data of ONE River deployment — FULLY ON-CHAIN via one
 * retrying multicall (no API/indexer). TroveManager list + owner-mutable
 * params come from lender-metadata (`riverConfig`/`riverMarkets`, keyed
 * lender → chain); this fetch reads the LIVE totals + current mint-fee rate +
 * oracle price. `fetchPrice` is nonpayable but simulates fine under the
 * multicall eth_call (same trick as Liquity).
 */
export async function fetchRiverMarkets(
  lender: string,
  chainId: string,
): Promise<RiverMarketsRaw> {
  const config = riverConfigFor(lender, chainId)
  const chainData = riverChainData(lender, chainId)
  const markets = chainData?.markets ?? []
  if (!config || markets.length === 0) {
    return { lender, config, chainData, spDeposits: null, markets: [] }
  }

  const calls = [
    // Single per-chain StabilityPool (diamond facet) — the earn side.
    { address: config.xapp, name: 'getTotalDebtTokenDeposits', params: [] },
    ...markets.flatMap((m) => [
      { address: m.troveManager, name: 'getEntireSystemDebt', params: [] },
      { address: m.troveManager, name: 'getEntireSystemColl', params: [] },
      {
        address: m.troveManager,
        name: 'getBorrowingRateWithDecay',
        params: [],
      },
      { address: m.troveManager, name: 'interestRate', params: [] },
      { address: m.troveManager, name: 'fetchPrice', params: [] },
      { address: m.troveManager, name: 'getTroveOwnersCount', params: [] },
    ]),
  ]

  let results: any[] = []
  try {
    results = (await multicallRetryUniversal({
      chain: chainId,
      calls,
      abi: RIVER_PUBLIC_READ_ABI,
      allowFailure: true,
    })) as any[]
  } catch {
    return { lender, config, chainData, spDeposits: null, markets: [] }
  }

  // Failed (allowFailure) slots arrive as '0x' — mapped to null.
  const val = (i: number): bigint | null => {
    const r = results[i]
    if (typeof r === 'bigint') return r
    if (typeof r === 'number') return BigInt(r)
    return null
  }

  const out: RiverMarketRaw[] = markets.map((market, i) => {
    const base = 1 + i * READS_PER_TM
    return {
      market,
      entireDebt: val(base),
      entireColl: val(base + 1),
      mintFeeRate: val(base + 2),
      interestRate: val(base + 3),
      price: val(base + 4),
      troveCount: val(base + 5),
    }
  })

  return { lender, config, chainData, spDeposits: val(0), markets: out }
}
